/**
 * StepItemRow
 * 항목(스텝) 하나의 입력 UI.
 *   - 메인 토글 버튼 (전화통화/카톡 등)
 *   - 선택 시: 상대방 입력(여러 개) + 상대방 추가 + 내용 입력 + 시간 타이머
 *
 * PC     : 한 줄 가로 배치(내용 입력만 줄바꿈으로 전체폭)
 * Mobile : 선택 시 세로 스택, 모든 입력 풀폭 + 큰 터치 타깃 (한 손 입력 우선)
 */
export default function StepItemRow({
  opt,
  sel, // 선택된 경우 {key, targets[], note, minutes, ...}, 아니면 undefined
  isMobile,
  onToggle,
  onAddTarget,
  onRemoveTarget,
  onUpdateTarget,
  onUpdateStep,
}) {
  const isSel = !!sel;

  // 메인 토글 버튼 (PC/모바일 공통, 크기만 분기)
  const mainBtn = (
    <button
      type="button"
      onClick={() => onToggle(opt.key)}
      style={{
        padding: isMobile ? "10px 16px" : "6px 14px",
        borderRadius: 20,
        cursor: "pointer",
        border: `0.5px solid ${isSel ? opt.color : "#e2e8f0"}`,
        background: isSel ? opt.color : "#fff",
        color: isSel ? "#fff" : "#0f172a",
        fontSize: isMobile ? 15 : 13,
        fontWeight: 600,
        whiteSpace: "nowrap",
        width: isMobile ? "100%" : 140,
        flexShrink: 0,
        textAlign: isMobile ? "center" : "left",
        transition: "all 0.12s",
        marginTop: isMobile ? 0 : 2,
      }}
    >
      {opt.icon} {opt.label}
    </button>
  );

  // 미선택 상태 → 버튼만
  if (!isSel) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          background: "transparent",
          padding: 0,
          borderRadius: 0,
          border: "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, flexWrap: "wrap" }}>
          {mainBtn}
        </div>
      </div>
    );
  }

  // 상대방 입력칸 묶음
  const targetInputs = (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: isMobile ? 6 : 4,
        alignItems: "center",
        flex: isMobile ? "unset" : 1,
        width: isMobile ? "100%" : "auto",
      }}
    >
      {sel.targets.map((targetVal, idx) => (
        <div
          key={idx}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flex: isMobile ? "1 1 45%" : "unset",
          }}
        >
          <input
            type="text"
            value={targetVal}
            onChange={(e) => onUpdateTarget(opt.key, idx, e.target.value)}
            placeholder={`상대방 ${sel.targets.length > 1 ? idx + 1 : ""}`}
            style={{
              width: isMobile ? "100%" : 90,
              border: "0.5px solid #cbd5e1",
              borderRadius: 6,
              padding: isMobile ? "8px 10px" : "4px 8px",
              fontSize: isMobile ? 15 : 12,
              height: isMobile ? 38 : 28,
              boxSizing: "border-box",
              outline: "none",
              background: "#fff",
              color: "#0f172a",
              marginTop: isMobile ? 0 : 4,   // PC에서 살짝 아래로


            }}
          />
          {sel.targets.length > 1 && (
            <button
              type="button"
              onClick={() => onRemoveTarget(opt.key, idx)}
              style={{
                background: "none",
                border: "none",
                color: "#ef4444",
                cursor: "pointer",
                fontSize: isMobile ? 14 : 11,
                padding: "0 2px",
                width: "fit-content",
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const addTargetBtn = (
    <button
      type="button"
      onClick={() => onAddTarget(opt.key)}
      style={{
        padding: isMobile ? "8px 12px" : "4px 8px",
        borderRadius: 6,
        border: "0.5px solid #cbd5e1",
        background: "#f1f5f9",
        color: "#0f172a",
        fontSize: isMobile ? 13 : 11,
        fontWeight: 500,
        cursor: "pointer",
        height: isMobile ? 38 : 28,
        whiteSpace: "nowrap",
        flexShrink: 0,
        width: isMobile ? "100%" : "fit-content",
        marginTop: isMobile ? 0 : 2,
      }}
    >
      👤 상대방 추가
    </button>
  );

  const noteInput = (
    <input
      type="text"
      value={sel.note || ""}
      onChange={(e) => onUpdateStep(opt.key, "note", e.target.value)}
      placeholder="내용 입력"
      style={{
        flexBasis: isMobile ? "auto" : "100%",
        width: "100%",
        border: "0.5px solid #cbd5e1",
        borderRadius: 6,
        padding: isMobile ? "10px 12px" : "6px 10px",
        fontSize: isMobile ? 15 : 13,
        height: isMobile ? 42 : 32,
        boxSizing: "border-box",
        outline: "none",
        background: "#fff",
        color: "#0f172a",
        marginTop: isMobile ? 0 : 4,
      }}
    />
  );

  const timer = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 10 : 3,
        border: "0.5px solid #cbd5e1",
        borderRadius: 6,
        padding: isMobile ? "0 12px" : "0 8px",
        height: isMobile ? 42 : 28,
        boxSizing: "border-box",
        background: "#fff",
        flexShrink: 0,
        width: isMobile ? "100%" : "auto",
        marginTop: isMobile ? 0 : 2,
      }}
    >
      <button
        type="button"
        onClick={() => onUpdateStep(opt.key, "minutes", Math.max(1, sel.minutes - 1))}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#0f172a",
          fontSize: isMobile ? 20 : 14,
          fontWeight: "bold",
          width: isMobile ? 36 : "fit-content",
          height: isMobile ? 36 : "auto",
        }}
      >
        −
      </button>
      <span
        style={{
          fontSize: isMobile ? 14 : 12,
          fontWeight: 600,
          color: opt.color,
          minWidth: 36,
          textAlign: "center",
        }}
      >
        {sel.minutes}분
      </span>

      <button
        type="button"
        onClick={() => onUpdateStep(opt.key, "minutes", sel.minutes + 1)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#0f172a",
          fontSize: isMobile ? 20 : 14,
          fontWeight: "bold",
          width: isMobile ? 36 : "fit-content",
          height: isMobile ? 36 : "auto",
        }}
      >
        +
      </button>
    </div>
  );

  // 건수 입력칸 (timer와 동일한 ±스테퍼 형태)
  const countBox = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isMobile ? "center" : "flex-start",
        gap: isMobile ? 10 : 3,
        border: "0.5px solid #cbd5e1",
        borderRadius: 6,
        padding: isMobile ? "0 12px" : "0 8px",
        height: isMobile ? 42 : 28,
        boxSizing: "border-box",
        background: "#fff",
        flexShrink: 0,
        width: isMobile ? "100%" : "auto",
        marginTop: isMobile ? 0 : 2,
      }}
    >
      <button
        type="button"
        onClick={() => onUpdateStep(opt.key, "count", Math.max(1, (sel.count || 1) - 1))}
        style={{
          background: "none", border: "none", cursor: "pointer", color: "#0f172a",
          fontSize: isMobile ? 20 : 14, fontWeight: "bold",
          width: isMobile ? 36 : "fit-content", height: isMobile ? 36 : "auto",
        }}
      >
        −
      </button>
      <span style={{ fontSize: isMobile ? 14 : 12, fontWeight: 600, color: "#0f172a", minWidth: 36, textAlign: "center" }}>
        {sel.count || 1}건
      </span>
      <button
        type="button"
        onClick={() => onUpdateStep(opt.key, "count", (sel.count || 1) + 1)}
        style={{
          background: "none", border: "none", cursor: "pointer", color: "#0f172a",
          fontSize: isMobile ? 20 : 14, fontWeight: "bold",
          width: isMobile ? 36 : "fit-content", height: isMobile ? 36 : "auto",
        }}
      >
        +
      </button>
    </div>
  );


  // ───────── 모바일: 선택 시 세로 스택 ─────────
  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          background: "#fff",
          padding: 10,
          borderRadius: 10,
          border: "0.5px dashed #cbd5e1",
        }}
      >
        {mainBtn}
        {targetInputs}
        {addTargetBtn}
        {noteInput}
        {timer}
        {countBox}


      </div>
    );
  }

  // ───────── PC: 한 줄 가로(내용만 줄바꿈) ─────────
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        background: "#fff",
        padding: 6,
        borderRadius: 6,
        border: "0.5px dashed #cbd5e1",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, flexWrap: "wrap" }}>
        {mainBtn}
        {targetInputs}
        {addTargetBtn}
        {noteInput}
        {timer}
        {countBox}
      </div>
    </div>
  );
}